/* Species Gallery – Variant-aware Gallery
   ============================================================================ */

document.addEventListener('DOMContentLoaded', () => {
  const DEBUG = true;
  const log = (...args) => DEBUG && console.log('[species-gallery]', ...args);

  const gallery = document.querySelector('[data-species-gallery]');
  if (!gallery) {
    log('species gallery not found, aborting');
    return;
  }

  const slides = Array.from(gallery.querySelectorAll('.species-gallery__slide'));
  const thumbs = Array.from(gallery.querySelectorAll('.species-gallery__thumb'));

  if (!slides.length) {
    log('no slides found, aborting');
    return;
  }

  /* ------------------------------------------------------------------------
     0) Helper: current variant id
     ------------------------------------------------------------------------ */
  const getCurrentVariantId = () => {
    const ccInput = document.querySelector('.cc-vd input.cc-fallback-variant-id[name="id"]');
    if (ccInput?.value) return String(ccInput.value).trim();

    const nativeFormId = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (nativeFormId?.value) return String(nativeFormId.value).trim();

    return null;
  };

  /* ------------------------------------------------------------------------
     VIDEO: autoplay when active slide has video
     ------------------------------------------------------------------------ */
  const pauseAllVideos = () => {
    document.querySelectorAll('video').forEach(v => { try { v.pause(); } catch(e){} });
  };

  const tryPlayVideoInSlide = (slide) => {
    if (!slide) return false;

    const v = slide.querySelector('video');
    if (v) {
      try {
        v.muted = true;
        v.playsInline = true;
        const p = v.play();
        if (p?.catch) p.catch(() => {});
      } catch(e){}
      return true;
    }

    const deferred = slide.querySelector('deferred-media, video-media');
    if (deferred) {
      try {
        if (typeof deferred.loadContent === 'function') deferred.loadContent();
        else deferred.querySelector('button, .deferred-media__poster')?.click();
      } catch(e){}

      setTimeout(() => {
        const vv = slide.querySelector('video');
        if (vv) {
          try {
            vv.muted = true;
            vv.playsInline = true;
            const p = vv.play();
            if (p?.catch) p.catch(() => {});
          } catch(e){}
        }
      }, 250);

      return true;
    }

    return false;
  };

  const syncVideoPlayback = (idx) => {
    pauseAllVideos();
    const slide = slides[idx];
    if (slide && slide.querySelector('video, video-media, deferred-media')) {
      tryPlayVideoInSlide(slide);
    }
  };

  /* ------------------------------------------------------------------------
     1) Build variant → mediaId mapping from .filter_image
     ------------------------------------------------------------------------ */
  const variantMediaMap = {};
  document.querySelectorAll('.filter_image').forEach(el => {
    const vid = String(el.dataset.varid || '').trim();
    const mid = String(el.dataset.img || '').trim();
    if (!vid || !mid) return;
    (variantMediaMap[vid] ||= []).push(mid);
  });

  // Inverse map: mediaId → variantId (undefined = common/unassigned)
  const mediaVariantOwner = {};
  for (const [vid, mids] of Object.entries(variantMediaMap)) {
    mids.forEach(mid => { mediaVariantOwner[mid] = vid; });
  }

  const filterModal = (variantId) => {
    const modal = document.querySelector('.product-media-modal');
    if (!modal) return;
    const vid = String(variantId || '').trim();
    modal.querySelectorAll('[data-media-id]').forEach(item => {
      const mid = String(item.dataset.mediaId || '').trim();
      const owner = mediaVariantOwner[mid];
      // Show if common (no owner) OR owned by current variant
      const visible = !owner || owner === vid;
      item.style.display = visible ? '' : 'none';
    });
  };

  /* ------------------------------------------------------------------------
     2) Tag slides with data-varid using data-media-handle + mapping
     ------------------------------------------------------------------------ */
  slides.forEach(slide => {
    const handle = String(slide.dataset.mediaHandle || '').trim();
    let tag = 'common';

    if (slide.classList.contains('common')) {
      tag = 'common';
    } else if (handle) {
      for (const vid in variantMediaMap) {
        if (variantMediaMap[vid].includes(handle)) {
          tag = vid;
          break;
        }
      }
    }

    slide.dataset.varid = tag;
  });

  /* ------------------------------------------------------------------------
     3) Tag thumbnails - keep existing data-varid or derive from slides
     ------------------------------------------------------------------------ */
  thumbs.forEach((thumb) => {
    const existing = String(thumb.dataset.varid || '').trim();
    if (existing) return;

    const tIdx = parseInt(thumb.dataset.thumbIndex, 10);
    if (thumb.classList.contains('common')) {
      thumb.dataset.varid = 'common';
    } else if (!isNaN(tIdx) && slides[tIdx]) {
      thumb.dataset.varid = slides[tIdx].dataset.varid || 'common';
    } else {
      thumb.dataset.varid = 'common';
    }
  });

  /* ------------------------------------------------------------------------
     4) Filtering + slider state
     ------------------------------------------------------------------------ */
  let currentIndex = 0;

  const isCommon = (v) => String(v || '').trim() === 'common';

  const setElVisible = (el, visible) => {
    el.hidden = !visible;
    el.style.display = visible ? '' : 'none';
  };

  const getFilteredIndices = () =>
    slides
      .map((_, idx) => idx)
      .filter(idx => slides[idx].classList.contains('active-variant'));

  const highlightThumbForIndex = (idx) => {
    thumbs.forEach((thumb) => {
      const tIdx = parseInt(thumb.dataset.thumbIndex, 10);
      thumb.classList.toggle('is-active', tIdx === idx);
    });
  };

  const showSlide = (idx) => {
    const filtered = getFilteredIndices();
    if (!filtered.length) return;

    if (!filtered.includes(idx)) idx = filtered[0];
    currentIndex = idx;

    slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === idx);
    });

    highlightThumbForIndex(idx);
    syncVideoPlayback(idx);
    updateNavButtons();
    if (typeof updateDots === 'function') updateDots();
  };

  const applyVariantToGallery = (variantId) => {
    if (!variantId) return;

    const vid = String(variantId).trim();
    log('applyVariantToGallery', vid);

    // Slides: mark matching varid OR common as active-variant
    slides.forEach(slide => {
      const tag = String(slide.dataset.varid || 'common').trim();
      const match = tag === vid || isCommon(tag);
      slide.classList.toggle('active-variant', match);
      slide.classList.remove('is-active');
    });

    // Thumbs: show only matching varid OR common
    thumbs.forEach((thumb) => {
      const tVar = String(thumb.dataset.varid || 'common').trim();
      const match = tVar === vid || isCommon(tVar);
      setElVisible(thumb, match);
    });

    const filtered = getFilteredIndices();
    if (!filtered.length) {
      log('no slides matched this variant');
      return;
    }

    // Show first filtered slide in product media order
    currentIndex = filtered[0];

    showSlide(currentIndex);
    filterModal(vid);
    if (typeof buildDots === 'function') buildDots();
  };

  /* ------------------------------------------------------------------------
     5) Thumbnail click — use data-thumb-index to find matching slide
     ------------------------------------------------------------------------ */
  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const idx = parseInt(thumb.dataset.thumbIndex, 10);
      if (isNaN(idx) || idx < 0 || idx >= slides.length) return;
      if (!slides[idx].classList.contains('active-variant')) return;
      showSlide(idx);
    });
  });

  /* ------------------------------------------------------------------------
     6) Touch swipe navigation
     ------------------------------------------------------------------------ */
  const mainEl = gallery.querySelector('.species-gallery__main');
  if (mainEl) {
    let touchStartX = 0;

    mainEl.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });

    mainEl.addEventListener('touchend', (e) => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) < 40) return;

      const filtered = getFilteredIndices();
      if (!filtered.length) return;

      const pos = filtered.indexOf(currentIndex);

      if (diff > 0 && pos < filtered.length - 1) {
        showSlide(filtered[pos + 1]);
      } else if (diff < 0 && pos > 0) {
        showSlide(filtered[pos - 1]);
      }
    }, { passive: true });
  }

  /* ------------------------------------------------------------------------
     6b) Prev / next arrow buttons
     ------------------------------------------------------------------------ */
  const prevBtn = gallery.querySelector('[data-gallery-prev]');
  const nextBtn = gallery.querySelector('[data-gallery-next]');

  const updateNavButtons = () => {
    if (!prevBtn || !nextBtn) return;
    const filtered = getFilteredIndices();
    const pos = filtered.indexOf(currentIndex);
    prevBtn.disabled = pos <= 0;
    nextBtn.disabled = pos >= filtered.length - 1;
  };

  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      const filtered = getFilteredIndices();
      const pos = filtered.indexOf(currentIndex);
      if (pos > 0) showSlide(filtered[pos - 1]);
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      const filtered = getFilteredIndices();
      const pos = filtered.indexOf(currentIndex);
      if (pos < filtered.length - 1) showSlide(filtered[pos + 1]);
    });
  }

  /* ------------------------------------------------------------------------
     7a) Mobile dot navigation
     ------------------------------------------------------------------------ */
  const dotsContainer = gallery.querySelector('[data-gallery-dots]');

  const buildDots = () => {
    if (!dotsContainer) return;
    dotsContainer.innerHTML = '';
    const filtered = getFilteredIndices();
    filtered.forEach((absIdx) => {
      const dot = document.createElement('button');
      dot.className = 'species-gallery__dot';
      dot.dataset.index = absIdx;
      dot.setAttribute('aria-label', 'Go to slide ' + (filtered.indexOf(absIdx) + 1));
      if (absIdx === currentIndex) dot.classList.add('is-active');
      dot.addEventListener('click', () => showSlide(absIdx));
      dotsContainer.appendChild(dot);
    });
  };

  const updateDots = () => {
    if (!dotsContainer) return;
    dotsContainer.querySelectorAll('.species-gallery__dot').forEach((dot) => {
      dot.classList.toggle('is-active', parseInt(dot.dataset.index, 10) === currentIndex);
    });
  };

  /* ------------------------------------------------------------------------
     7b) Variant change hooks
     ------------------------------------------------------------------------ */
  document.addEventListener('variant:change', (e) => {
    const variant = e.detail && e.detail.variant;
    if (!variant) return;
    applyVariantToGallery(variant.id);
  });

  // Also listen to Shopify's pub/sub if available
  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event.data?.variant) {
        applyVariantToGallery(event.data.variant.id);
      }
    });
  }

  const variantIdInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
  if (variantIdInput) {
    variantIdInput.addEventListener('change', () => applyVariantToGallery(variantIdInput.value));
  }

  /* ------------------------------------------------------------------------
     8) Init
     ------------------------------------------------------------------------ */
  const initVid = getCurrentVariantId();
  if (initVid) {
    applyVariantToGallery(initVid);
  } else {
    log('no current variant id found; showing all as common');
    slides.forEach(slide => {
      slide.classList.add('active-variant');
      slide.classList.remove('is-active');
    });
    thumbs.forEach(thumb => setElVisible(thumb, true));
    showSlide(0);
    filterModal(null);
    buildDots();
  }
});
